import { useCallback, useState } from 'react';

import { deleteFileFromBrowserStorage, VideoRecordingEvent } from '@bonusly/common-action-input';
import { useDisclosure } from '@bonusly/design-system';
import { useEventListener } from '@bonusly/events';

import { useGlobalMediaStore } from '../../../../modules/bonus-feed/hooks/use-global-media-store';
import { GiveBoxAttachment, GiveBoxEvent, GiveBoxOpenEventData } from '../events';
import { createDraftContent, createMultiRecipientRecognitionContent, createRecognitionContent } from '../utils/content';
import { clearDraft } from '../utils/recognition-storage';

type GiveModalProps = {
	onSuccess?: () => void;
};

export const useGiveModal = ({ onSuccess }: GiveModalProps = {}) => {
	const state = useDisclosure();

	// This isn't the ideal to do this, but is the easiest to programmatically set
	// the default content when a users triggers an event to give to a specific user.
	// The give box instance is not immediately available when the event is triggered.
	// This is a temporary solution until we can refactor the give box respond to events
	// in a more flexible way.
	const [content, setContent] = useState<any>(undefined);
	const [attachment, setAttachment] = useState<GiveBoxAttachment | undefined>(undefined);
	// Bizy-drafted text to insert once the modal mounts (see HABITS-1294).
	// A plain number is used as a replay token so re-opens with the same
	// text still re-trigger the insert effect downstream.
	const [pendingAiDraft, setPendingAiDraft] = useState<{ text: string; token: number; autoSend?: boolean } | undefined>(undefined);
	const { getActiveElement, pauseVideo } = useGlobalMediaStore();

	const initGiveToUser = useCallback(
		(data?: GiveBoxOpenEventData) => {
			const aiOpts = data?.aiGenerated ? { aiGenerated: true } : undefined;
			if (data?.editorContent) {
				setContent(data.editorContent);
			} else if (data?.recipients && data.recipients.length > 0) {
				// External-browser deep-link hand-off (Bizy Desktop): multiple
				// resolved recipients carrying real ids. Takes precedence over
				// the singular `recipient` path used by the in-app flow. See BIZY-422.
				const multiRecipientContent = createMultiRecipientRecognitionContent(data.recipients, data.text, data.amount, data.hashtags, aiOpts);
				setContent(multiRecipientContent);
			} else if (data && data.recipient && data.text) {
				const content = createRecognitionContent(data.recipient, data.text, data.amount, data.hashtags, aiOpts);
				setContent(content);
			} else if (data && data.recipient) {
				const content = createRecognitionContent(data.recipient, undefined, data.amount, data.hashtags);
				setContent(content);
			} else if (data && ((data.amount !== undefined && data.amount > 0) || (data.hashtags && data.hashtags.length > 0))) {
				// Rich deep-link whose usernames all failed to resolve: there are no
				// recipients, but we still have an amount and/or hashtags. Render them
				// (plus any message text) instead of dropping them. createDraftContent
				// lays down no recipient node when given an empty recipients array. See BIZY-422.
				const amountHashtagContent = createDraftContent([], data.text, data.amount, data.hashtags, aiOpts);
				setContent(amountHashtagContent);
			} else if (data && data.text) {
				const marks = data.aiGenerated ? [{ type: 'suggestion-highlight' }] : undefined;
				setContent({
					type: 'doc',
					content: [
						{
							type: 'paragraph',
							content: [{ type: 'text', text: data.text, marks }],
						},
					],
				});
			} else if (data?.aiGenerated) {
				// AI / deep-link hand-off (Bizy Desktop) that resolved nothing to
				// pre-fill: no recipients, text, amount, or hashtags. Set an explicit
				// empty doc so GiveModalContent's mount-time `loadDraft()` (which only
				// runs when `content` is falsy) is bypassed — otherwise a clean Bizy
				// open would surface a stale "accidental dismissal" draft. Manual opens
				// (no `aiGenerated`) still fall through with `content` undefined and
				// restore their saved draft as before. See BIZY-422.
				setContent({
					type: 'doc',
					content: [{ type: 'paragraph' }],
				});
			}

			// Set initial attachment if provided, otherwise clear it
			setAttachment(data?.attachment);

			if (data?.aiDraft) {
				// `autoSend` propagates to GiveModalContent so a Bizy "Give now"
				// action can submit the form after the draft + recipient /
				// amount / hashtags mount. See GiveBoxEvent.Open docs.
				setPendingAiDraft({ text: data.aiDraft, token: Date.now(), autoSend: data.autoSend });
			} else {
				setPendingAiDraft(undefined);
			}

			state.onOpen();

			// pause any video elements in the viewport
			const activeVideoElement = getActiveElement();
			if (activeVideoElement) {
				pauseVideo(activeVideoElement);
			}
		},
		[state, getActiveElement, pauseVideo]
	);

	const handleClose = () => {
		setContent(undefined);
		setAttachment(undefined);
		setPendingAiDraft(undefined);
		state.onClose();
	};

	useEventListener(GiveBoxEvent.Open, initGiveToUser);
	useEventListener(VideoRecordingEvent.ClosedCarouselModalWithNoVideo, handleClose);

	const handleOnSuccess = () => {
		handleClose();
		onSuccess?.();
		deleteFileFromBrowserStorage();
		clearDraft();
	};
	return {
		content,
		initialAttachment: attachment,
		pendingAiDraft,
		onOpen: state.onOpen,
		onClose: handleClose,
		onSuccess: handleOnSuccess,
		isOpen: state.isOpen,
	};
};
